// PayPal client helpers. Loads the official PayPal JS SDK on demand and
// proxies all create/capture/cancel/payout operations through edge functions.
import { supabase } from "@/integrations/supabase/client";

export type PayPalEnv = "sandbox" | "live";

let configPromise: Promise<{ client_id: string; env: PayPalEnv }> | null = null;

export async function getPaypalClientConfig() {
  if (!configPromise) {
    configPromise = (async () => {
      const { data, error } = await supabase.functions.invoke("paypal-config");
      if (error) throw error;
      if (!data?.client_id) throw new Error("PayPal not configured");
      return data as { client_id: string; env: PayPalEnv };
    })();
  }
  return configPromise;
}

let sdkPromise: Promise<typeof window.paypal> | null = null;

export async function loadPaypalSdk(options: {
  intent?: "capture" | "subscription";
  currency?: string;
  vault?: boolean;
} = {}) {
  if (sdkPromise && (window as unknown as { paypal?: unknown }).paypal) return sdkPromise;
  const { client_id } = await getPaypalClientConfig();
  const params = new URLSearchParams({
    "client-id": client_id,
    currency: options.currency ?? "USD",
    intent: options.intent ?? "capture",
    components: "buttons",
  });
  if (options.vault) params.set("vault", "true");
  if (options.intent === "subscription") {
    params.set("vault", "true");
    params.set("intent", "subscription");
  }
  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-paypal-sdk]");
    if (existing) existing.remove();
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
    s.dataset.paypalSdk = "true";
    s.async = true;
    s.onload = () => resolve((window as unknown as { paypal: typeof window.paypal }).paypal);
    s.onerror = () => reject(new Error("Failed to load PayPal SDK"));
    document.head.appendChild(s);
  });
  return sdkPromise;
}

export async function createPaypalOrder(args: {
  amountCents: number;
  currency?: string;
  featureKey?: string;
  paymentType?: string;
}): Promise<string> {
  const { data, error } = await supabase.functions.invoke("paypal-create-order", {
    body: {
      amount_cents: args.amountCents,
      currency: args.currency ?? "USD",
      feature_key: args.featureKey,
      payment_type: args.paymentType,
    },
  });
  if (error) throw error;
  if (!data?.order_id) throw new Error("No PayPal order id returned");
  return data.order_id as string;
}

export async function capturePaypalOrder(orderId: string) {
  const { data, error } = await supabase.functions.invoke("paypal-capture-order", {
    body: { order_id: orderId },
  });
  if (error) throw error;
  return data as { status: string; order_id: string; capture_id: string | null };
}

export async function registerPaypalSubscription(subscriptionId: string, planId: string) {
  const { data, error } = await supabase.functions.invoke("paypal-create-subscription", {
    body: { subscription_id: subscriptionId, plan_id: planId },
  });
  if (error) throw error;
  return data as { status: string; subscription_id: string };
}

export async function cancelPaypalSubscription(subscriptionId: string, reason?: string) {
  const { data, error } = await supabase.functions.invoke("paypal-cancel-subscription", {
    body: { subscription_id: subscriptionId, reason },
  });
  if (error) throw error;
  return data as { status: string };
}

export async function sendPaypalPayout(args: {
  recipientEmail: string;
  amountCents: number;
  currency?: string;
  note?: string;
}) {
  const { data, error } = await supabase.functions.invoke("paypal-payout", {
    body: {
      recipient_email: args.recipientEmail,
      amount_cents: args.amountCents,
      currency: args.currency ?? "USD",
      note: args.note,
    },
  });
  if (error) throw error;
  return data as { payout_id: string; batch_id: string; status: string };
}

declare global {
  interface Window {
    paypal: {
      Buttons: (config: Record<string, unknown>) => {
        render: (el: HTMLElement | string) => Promise<void>;
        close: () => void;
        isEligible: () => boolean;
      };
    };
  }
}
